import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput2 } from 'components/PlainInput/PlainInput2'
import { SaveChanges } from 'components/SaveChanges'
import { convertNullToBlank, convertNullValue } from 'pages/Resources/GuidelineOverlay/logic'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getLoanTypeMapForSelect, loanTypeMap } from 'reducers/loanDetail.reducer'
import { getAdminConfig, setAdminConfig } from 'services'
import { MultiSelect } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { LockRateRestriction } from './LockRateRestriction'

interface IRules {
  relockRate: Record<string, string[]>
  relockDurationDays: number
  lockExpWarning: Record<string, string[]>
  buttons?: {
    reviewAndLock: string[]
  }
}

const configs: Record<string, string> = {
  lockRateRestrictions: 'Lock Rate Restrictions in Loan Structure.',
  relockRate: '"ReLock Rate" is required in below channels and Loan Status.',
  lockExpWarning: 'Show Lock EXP as Red color in pipeline table.',
  buttons: 'Buttons',
}

export const Rules = () => {
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])

  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState(false)
  const [data, setData] = useState<IRules>()

  useEffect(() => {
    getAdminConfig('secondaryMarketing')
      .then((v) => setData(v.rules))
      .finally(() => setLoading(false))
  }, [])

  const onChange = (key: string, loanType: string, value: any) => {
    if (!data) return
    const newData = cloneDeep(data) as any

    if (!loanType) {
      newData[key] = value
    } else {
      if (!newData[key]) newData[key] = {}
      newData[key][loanType] = value
    }

    setData(newData)
    setChanged(true)
  }

  const onSubmit = () => {
    setLoading(true)
    setAdminConfig('secondaryMarketing', { rules: data })
      .then(() => {
        toast('Successfully saved!', { type: 'info' })
        setChanged(false)
      })
      .finally(() => setLoading(false))
  }

  return (
    <div>
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSubmit} />

      {Object.keys(configs).map((key, index) => (
        <div className="mb-4">
          <p className="border-b-2 border-shade-blue w-full italic flex justify-between items-center mb-2">
            {index + 1}. {configs[key]}
          </p>

          {key == 'buttons' ? (
            <div className="grid grid-cols-4 gap-4 mb-4 items-center">
              <p className="pl-4">Review & Lock</p>
              <MultiSelect
                id="review-and-lock-eligible-loan-status"
                title="Eligible Loan Status"
                options={loanGlobalStatus}
                value={data?.buttons?.reviewAndLock || []}
                defaultSelected={false}
                onChange={(value: string[]) => onChange(key, '', { reviewAndLock: value })}
              />
            </div>
          ) : key == 'lockRateRestrictions' ? (
            <div className="flex flex-col gap-4">
              {Object.keys(getLoanTypeMapForSelect()).map((loanType) => (
                <div key={`${key}-${loanType}`}>
                  <p className="font-semibold mb-2 pl-2">{loanTypeMap[loanType]}</p>
                  <RenderInput
                    Key={`${key}-${loanType}`}
                    input={{
                      inputType: 'custom',
                      title: '',
                      span: 'full',
                      value: (data as any)?.[key]?.[loanType],
                      render: (value: any, onChange: Function) => (
                        <LockRateRestriction value={value} onChange={onChange} />
                      ),
                    }}
                    onChange={(_: string, value: any) => onChange(key, loanType, value)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-4">
                {Object.keys(getLoanTypeMapForSelect()).map((loanType) => (
                  <RenderInput
                    Key={`${key}${loanType}`}
                    input={{
                      inputType: 'multiselect',
                      options: loanGlobalStatus,
                      title: loanTypeMap[loanType],
                      value: (data as any)?.[key]?.[loanType],
                      defaultSelected: false,
                    }}
                    onChange={(_: string, value: any) => onChange(key, loanType, value)}
                  />
                ))}
              </div>
              {key === 'relockRate' && (
                <p className="mt-4">
                  <code>
                    If a loan is relocked within{' '}
                    <span className="w-fit max-w-[50px] relative inline-block text-center">
                      <PlainInput2
                        value={convertNullToBlank(data?.relockDurationDays ?? 30)}
                        origin={convertNullToBlank(data?.relockDurationDays ?? 30)}
                        content={convertNullValue(data?.relockDurationDays ?? 30)}
                        onChange={(value: any) => onChange('relockDurationDays', '', value)}
                        className="font-semibold"
                      />
                    </span>{' '}
                    days of the original lock expiration, pricing will be the worse of the original rate sheet and the
                    current rate sheet. If the relock occurs more than{' '}
                    <span className="w-fit max-w-[50px] relative inline-block text-center">
                      <PlainInput2
                        value={convertNullToBlank(data?.relockDurationDays ?? 30)}
                        origin={convertNullToBlank(data?.relockDurationDays ?? 30)}
                        content={convertNullValue(data?.relockDurationDays ?? 30)}
                        onChange={(value: any) => onChange('relockDurationDays', '', value)}
                        className="font-semibold"
                      />
                    </span>{' '}
                    days after expiration, pricing will be based on the current rate sheet.
                  </code>
                </p>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  )
}
